'use client';

import React, {
  type ChangeEvent,
  forwardRef,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IconButton } from 'components/Button';
import {
  IconButton as IconButton2,
  type IconButtonProps,
} from 'components/Button2/IconButton';
import { Spinner } from 'components/Spinner';
import { TextField, type TextFieldProps } from 'components/TextField/TextField';
import { useControlledComponent } from 'hooks/useControlledComponent';
import { useDebounceCallback } from 'hooks/useDebounceCallback';
import { usePrevious } from 'hooks/usePrevious';
import styled from 'styled-components';
import { isComposingEvent } from 'utils/is-composing';
import { noop } from 'utils/noop';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import {
  IconArrowLeft,
  IconMagnifyingglass,
  IconMagnifyingglassAiSparkle,
  IconX,
} from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';

/**
 * This type defines two mutually exclusive states for the clear button:
 * - Clear button enabled (default): clearLabel is required
 * - Clear button disabled: clearLabel is not needed
 */
export type ClearButtonProps =
  | {
      /**
       * The clear button is enabled by default, but can be disabled in
       * certain situations. For example, in a modal when the clear button
       * could be mistaken for a close modal button.
       */
      enableClearButton?: true;
      /** The accessibility label for the clear icon button */
      clearButtonAriaLabel: string;
    }
  | {
      /** Clear button is disabled */
      enableClearButton: false;
      /** Clear label is not needed when clear button is disabled */
      clearButtonAriaLabel?: string;
    };

export type SearchFieldProps = {
  /**
   * The duration in ms to debounce the onRequestSearch callback. Note that
   * this does not apply to when the user clears the input value, in which
   * case the callback is called immediately.
   */
  debounce?: number;
  /**
   * Optionally control whether an active fetch is happening for
   * API hosted data.
   */
  isSearching?: boolean;
  variant?: SearchFieldVariants;
  /**
   * The spinner is enabled by default, but you might want to disable
   * it altogether for simple client-side searches.
   */
  enableSpinner?: boolean;
  /**
   * Called with the search query after the debounce has finished.
   */
  onRequestSearch?: (value: string) => void;
  /**
   * When the user starts typing, onRequestSearch isn't called until
   * the debounce has finished. However, we actually show the spinner
   * when the debounce function is first called. You might want to leverage
   * this callback to update the UI accordingly, by, say, dimming
   * the existing search results.
   */
  onDebounceStart?: () => void;
  onDebounceEnd?: () => void;
  /**
   * Similar to onChange, but it provides the updated query as its
   * only argument. This can be used to control the component.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Called when the user presses the clear button
   */
  onQueryClear?: (evt: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Optionally provide a query value when used in "controlled"
   * mode.
   */
  query?: string;
  /** Provide a default input value for uncontrolled field usage. */
  defaultQuery?: string;
  /**
   * The accessibility label for the loading spinner.
   * Required for internationalization (e.g., "Searching").
   */
  pendingStateLabel: string;
  /** Has an error */
  error?: string;
  /** Optional element to render the search input */
  adornmentBefore?: React.ReactNode;
  /** The variant of the search icon */
  iconVariant?: 'default' | 'sparkle';
} & NavigateBackProps &
  ClearButtonProps &
  Omit<TextFieldProps, 'adornmentAfter' | 'adornmentBefore'>;

type SearchFieldVariants = 'pill' | 'default';

/**
 * This type defines two mutually exclusive states:
 * - Navigation back functionality enabled (both props required)
 * - No navigation back functionality (both props undefined)
 */
export type NavigateBackProps =
  | {
      /**
       * Callback function triggered when the back navigation button is pressed.
       * This is typically used to return to a previous view or state.
       * @example
       * ```tsx
       * <SearchField
       *   onNavigateBack={() => history.back()}
       *   navigateBackLabel="Back to previous view"
       * />
       * ```
       */
      onNavigateBack: IconButtonProps['onPress'];
      /**
       * Accessible label for the back navigation button.
       * Required when onNavigateBack is provided to ensure proper accessibility.
       * @example
       * ```tsx
       * <SearchField
       *   onNavigateBack={() => history.back()}
       *   navigateBackLabel="Back to previous view"
       * />
       * ```
       */
      navigateBackLabel: IconButtonProps['label'];
    }
  | {
      /** Navigation back functionality is disabled */
      onNavigateBack?: undefined;
      /** Navigation back label is not needed when functionality is disabled */
      navigateBackLabel?: never;
    };

export const SearchField = forwardRef(function SearchFieldComponent(
  {
    isSearching = false,
    debounce = 500,
    onRequestSearch = noop,
    variant = 'default',
    defaultQuery = '',
    enableSpinner = true,
    enableClearButton = true,
    onDebounceStart = noop,
    onDebounceEnd = noop,
    onQueryChange = noop,
    clearButtonAriaLabel,
    pendingStateLabel,
    query,
    size,
    error,
    onNavigateBack,
    navigateBackLabel,
    adornmentBefore,
    onQueryClear,
    iconVariant = 'default',
    ...otherProps
  }: SearchFieldProps,
  forwardedRef
) {
  const [value, setValue] = useControlledComponent(
    query,
    onQueryChange,
    defaultQuery
  );
  const [handleRequestChange, isRequestSearchDebounced] = useDebounceCallback(
    onRequestSearch,
    debounce
  );

  const thisInputRef = useRef<HTMLInputElement>(null);
  const textFieldRef = mergeRefs(forwardedRef, thisInputRef);

  /**
   * Track composition state to prevent premature search triggers during
   * IME input (e.g. accent + letter → ü) or macOS accent menu (hold E → select é)
   */
  const isComposingRef = useRef(false);
  const [internalValue, setInternalValue] = useState(value);

  // Sync internal value when controlled value changes
  useEffect(() => {
    if (!isComposingRef.current) {
      setInternalValue(value);
    }
  }, [value]);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(
    (evt: React.CompositionEvent<HTMLInputElement>) => {
      isComposingRef.current = false;
      const finalValue = evt.currentTarget.value;

      // Update both internal and external state with final value
      setInternalValue(finalValue);
      setValue(finalValue);
      handleRequestChange(finalValue);
    },
    [handleRequestChange, setValue]
  );

  const handleClear = useCallback(
    (evt: MouseEvent<HTMLButtonElement>) => {
      setInternalValue(``);
      setValue(``);
      onQueryClear?.(evt);
      handleRequestChange(``);
      thisInputRef.current?.focus();
    },
    [handleRequestChange, onQueryClear, setValue]
  );

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const newValue = evt.target.value;

      // Always update internal value to reflect what user sees
      setInternalValue(newValue);

      // Only update external state and trigger search when not composing
      if (!isComposingEvent(evt)) {
        setValue(newValue);
        handleRequestChange(newValue);
      }
    },
    [handleRequestChange, setValue]
  );

  const mergedTextFieldProps = mergeProps(otherProps, {
    onChange: handleChange,
    onCompositionStart: handleCompositionStart,
    onCompositionEnd: handleCompositionEnd,
  });

  const wasDebounced = usePrevious(isRequestSearchDebounced);

  /**
   * This allows us to hook into the debounce state to allow consumers to define their custom events.
   * For example, if you want to show a loading state when the debounce starts, you can do so here.
   */
  useEffect(() => {
    if (wasDebounced === isRequestSearchDebounced) return;
    if (isRequestSearchDebounced) {
      onDebounceStart();
    } else {
      onDebounceEnd();
    }
  }, [isRequestSearchDebounced, onDebounceEnd, onDebounceStart, wasDebounced]);

  const SearchIcon =
    iconVariant === 'sparkle'
      ? IconMagnifyingglassAiSparkle
      : IconMagnifyingglass;

  return (
    <StyledTextField
      ref={textFieldRef}
      $variant={variant}
      adornmentAfter={
        !error && (
          <SearchFieldAdornmentAfter
            clearButtonAriaLabel={clearButtonAriaLabel}
            enableClearButton={enableClearButton}
            hasSearchValue={Boolean(value)}
            hasSpinner={
              enableSpinner && (isRequestSearchDebounced || isSearching)
            }
            onClear={handleClear}
            pendingStateLabel={pendingStateLabel}
          />
        )
      }
      adornmentBefore={
        <>
          {onNavigateBack ? (
            <IconButton2
              icon={<IconArrowLeft size={size === 'small' ? 16 : 24} />}
              label={navigateBackLabel}
              onPress={onNavigateBack}
              shape="circular"
              size="small"
              variant="tertiary"
            />
          ) : (
            <SearchIcon size={size === 'small' ? 16 : 24} />
          )}
          {adornmentBefore}
        </>
      }
      error={error}
      size={size}
      type="search"
      value={internalValue}
      {...mergedTextFieldProps}
    />
  );
});

SearchField.displayName = 'SearchField';

type SearchFieldAdornmentAfterProps = {
  clearButtonAriaLabel?: string;
  enableClearButton?: boolean;
  hasSearchValue: boolean;
  hasSpinner: boolean;
  onClear: (evt: MouseEvent<HTMLButtonElement>) => void;
  pendingStateLabel: string;
};

/**
 * Renders the adornment after the input field to show various states
 */
function SearchFieldAdornmentAfter({
  clearButtonAriaLabel,
  enableClearButton,
  hasSearchValue,
  hasSpinner,
  onClear,
  pendingStateLabel,
}: SearchFieldAdornmentAfterProps) {
  const adornmentState = getAdornmentState(hasSpinner, hasSearchValue);

  return (
    <AdornmentAfterContainer>
      <Spinner
        aria-label={pendingStateLabel}
        centered
        delay={0}
        isLoading={adornmentState === 'spinner'}
        size="x-small"
      />
      {enableClearButton && (
        <FadeIn
          $hasValue={hasSearchValue}
          $visible={adornmentState === 'clear'}
          aria-hidden={adornmentState !== 'clear'}
        >
          <IconButton
            icon={<IconX size={16} />}
            label={clearButtonAriaLabel!}
            onClick={onClear}
            size="small"
            variant="link"
          />
        </FadeIn>
      )}
    </AdornmentAfterContainer>
  );
}

SearchFieldAdornmentAfter.displayName = 'SearchFieldAdornmentAfter';

type AdornmentState = 'spinner' | 'clear' | 'none';

/**
 * Determines which adornment to display in the search field based on current state.
 * Priority order: spinner (when searching and has value) > clear button (when has value) > none
 */
function getAdornmentState(
  hasSpinner: boolean,
  hasSearchValue: boolean
): AdornmentState {
  if (hasSpinner && hasSearchValue) {
    return 'spinner';
  }
  if (hasSearchValue) {
    return 'clear';
  }
  return 'none';
}

const StyledTextField = styled(TextField)<{ $variant: SearchFieldVariants }>`
  ${(p) => p.$variant === 'pill' && `border-radius: ${theme.spaces[32]};`}
`;

const AdornmentAfterContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  width: ${theme.spaces[24]};
`;

const FadeIn = styled.div<{ $visible: boolean; $hasValue: boolean }>`
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition-duration: ${(p) =>
    p.$hasValue ? theme.transitions.durations[100] : '50ms'};
  transition-property: opacity, visibility;
  transition-timing-function: linear;
  ${(p) =>
    !p.$visible
      ? `
    opacity: 0;
    pointer-events: none;
    transition-delay: 0;
    visibility: hidden;
  `
      : `
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  `};
`;
